import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomisableForm } from 'src/components/form/form/CustomisableForm';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import type {
  FormContextProps,
  SubmitButton,
} from 'src/components/form/form/types';
import type { useGetFormCustomisationByParentTypes } from 'src/hooks/queries/form-configuration/useGetFormCustomisationByParentTypes';

import { CustomDatasourceFormFields } from './CustomDatasourceFormFields';
import type { CustomDatasourceFormData } from './customDatasourceSchema';
import {
  customDatasourceFormSchema,
  defaultValues,
} from './customDatasourceSchema';
import type { CustomAttributeSchemaLookup } from './types';

type GetFormCustomisationData = NonNullable<
  ReturnType<typeof useGetFormCustomisationByParentTypes>['data']
>;

export type Props = {
  onDismiss: (saved: boolean) => Promise<void>;
  onSave: (data: CustomDatasourceFormData) => Promise<void>;
  onPreview: (data: CustomDatasourceFormData) => Promise<void>;
  values?: CustomDatasourceFormData;
  // Mount-only seed for a create form (copy prefill). `values` is edit
  // hydration and the preview round-trip.
  defaultValues?: FormContextProps<CustomDatasourceFormData>['defaultValues'];
  customAttributeSchemaLookup: CustomAttributeSchemaLookup;
  formFieldConfigurations:
    | GetFormCustomisationData['form_field_configuration']
    | null;
  readOnly: boolean;
  mode: 'create' | 'update';
};

export const CustomDatasourceForm: FC<Props> = ({
  onSave,
  onPreview,
  onDismiss,
  values,
  defaultValues: defaultValuesOverride,
  customAttributeSchemaLookup,
  formFieldConfigurations,
  readOnly,
  mode,
}) => {
  const { t } = useTranslation(['common'], { keyPrefix: 'customDatasources' });

  const defaultSubmitActions: SubmitButton<CustomDatasourceFormData>[] = [
    {
      label: t('save_button'),
      action: onSave,
    },
    {
      label: t('preview_button'),
      disableNotification: true,
      action: onPreview,
    },
  ];

  return (
    <CustomisableForm
      defaultValues={defaultValuesOverride ?? defaultValues}
      saveMode={mode}
      i18n={{
        entity_name: t('entity_name'),
      }}
      submitActions={defaultSubmitActions}
      values={values}
      onSave={onSave}
      readOnly={readOnly}
      schema={customDatasourceFormSchema}
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
      formId={''}
      onDismiss={onDismiss}
    >
      <CustomDatasourceFormFields
        mode={mode}
        customAttributeSchemaLookup={customAttributeSchemaLookup}
        formFieldConfigurations={formFieldConfigurations}
      />
    </CustomisableForm>
  );
};
